import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {useErrorHandler} from '@malt/utils';
import {Configuration, type FetchError, type ProfileSuggestion, ProfileSuggestionsApi} from '@api';
import {suggestionConfigKeys} from '../ui/suggestion-card.config';

const STORAGE_KEY = 'nudge_dismissed';
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_DISMISS_DURATION_MS = 90 * ONE_DAY_IN_MS;

type DismissedEntry = {dismissedAt: number; durationMs: number};
type DismissedStore = Partial<Record<ProfileSuggestion['type'], DismissedEntry>>;

function readDismissedStore(): DismissedStore {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
}

function filterSuggestions(items: ProfileSuggestion[]): ProfileSuggestion[] {
    const knownTypes = new Set<string>(suggestionConfigKeys);
    const filtered = items.filter((s) => knownTypes.has(s.type));
    if (!import.meta.client) return filtered;
    const store = readDismissedStore();
    const now = Date.now();
    return filtered.filter((s) => {
        const entry = store[s.type];
        return !entry || now - entry.dismissedAt >= entry.durationMs;
    });
}

export const useProfileSuggestionsStore = defineStore('profile-suggestions', () => {
    const apiConfiguration = new Configuration({basePath: ''});
    const profileSuggestionsApi = new ProfileSuggestionsApi(apiConfiguration);
    const {handleFetchError} = useErrorHandler();

    const suggestions = ref<ProfileSuggestion[]>([]);
    const hasSuggestions = computed(() => suggestions.value.length > 0);
    const taskListCompleted = ref(false);

    async function getSuggestions(): Promise<void> {
        try {
            const result = await profileSuggestionsApi.getProfileSuggestions();
            if (result.suggestions.length > 0) taskListCompleted.value = true;
            suggestions.value = filterSuggestions(result.suggestions);
        } catch (e) {
            handleFetchError(e as FetchError);
        }
    }

    function applyDismissFilter(): void {
        suggestions.value = filterSuggestions(suggestions.value);
    }

    function dismissSuggestion(type: ProfileSuggestion['type']): void {
        const durationMs = DEFAULT_DISMISS_DURATION_MS;
        if (import.meta.client) {
            const store = readDismissedStore();
            store[type] = {dismissedAt: Date.now(), durationMs};
            localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        }
        suggestions.value = suggestions.value.filter((s) => s.type !== type);
    }

    function shouldShowSuggestionsSection(): boolean {
        if (!import.meta.client) return true;
        if (!taskListCompleted.value) return true;
        return filterSuggestions(suggestions.value).length > 0;
    }

    return {hasSuggestions, taskListCompleted, suggestions, applyDismissFilter, dismissSuggestion, getSuggestions, shouldShowSuggestionsSection};
});
